import { ArrowRight, Building2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";

const formatAmount = (value) => `₹${Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} Cr`;
const formatCount = (value) => Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function CompanyCard({ company, viewMode = "grid" }) {
  return (
    <Card
      className={`company-card rounded-none border-slate-200 bg-white ${viewMode === "list" ? "w-full" : "h-full"}`}
      data-testid={`csr-company-card-${company.id}`}
    >
      <CardContent className={`p-5 ${viewMode === "list" ? "flex flex-col gap-5 md:flex-row md:items-start md:justify-between" : "space-y-5"}`}>
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#1E3A8A]">{formatCount(company.project_count)} project records</span>
          </div>

          <div>
            <h3 className="text-2xl font-bold tracking-tight text-slate-950" data-testid={`company-name-${company.id}`}>
              {company.company_name}
            </h3>
            <p className="mt-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500" data-testid={`company-cin-${company.id}`}>
              CIN: {company.cin}
            </p>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-600" data-testid={`company-sectors-${company.id}`}>
              {company.sectors.slice(0, 2).join(" • ")}
              {company.sectors.length > 2 ? ` • +${company.sectors.length - 2} more sectors` : ""}
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="border border-slate-200 bg-slate-50 p-3" data-testid={`company-total-spend-${company.id}`}>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Total spend</p>
              <p className="mt-2 text-lg font-black text-slate-950">{formatAmount(company.total_spend_crore)}</p>
            </div>
            <div className="border border-slate-200 bg-slate-50 p-3" data-testid={`company-coverage-${company.id}`}>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">District coverage</p>
              <p className="mt-2 text-lg font-black text-slate-950">{formatCount(company.district_count)} districts</p>
            </div>
            <div className="border border-slate-200 bg-slate-50 p-3" data-testid={`company-average-${company.id}`}>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Total outlay</p>
              <p className="mt-2 text-lg font-black text-slate-950">{formatAmount(company.total_outlay_crore)}</p>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Building2 size={16} className="text-[#1E3A8A]" />
              <span>{company.years.join(", ")}</span>
            </div>
          </div>
        </div>

        <div className="flex min-w-[190px] flex-col justify-end gap-4 md:items-end">
          <div className="flex w-full flex-col gap-3 md:max-w-[220px]">
            {company.selectedRecordsHref ? (
              <Link to={company.selectedRecordsHref} className="w-full" data-testid={`view-selected-records-link-${company.id}`}>
                <Button className="w-full rounded-none bg-[#F26522] text-white hover:bg-[#D9561B]" data-testid={`view-selected-records-button-${company.id}`}>
                  View selected records
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            ) : null}

            <Link to={`/company/${company.id}`} className="w-full" data-testid={`view-profile-link-${company.id}`}>
              <Button className="w-full rounded-none bg-slate-950 text-white hover:bg-slate-800" data-testid={`view-profile-button-${company.id}`}>
                View full profile
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}